import { faTint } from '@fortawesome/free-solid-svg-icons';

/**
 * Trade Ops Bunkers (fuels) walkthrough.
 *
 * Shares the TradeOperationsLayout component with Cargo. Bunkers does NOT
 * have a Bid/Offer tab - the fuels flow is Enquiry -> Recap -> Delivery, by
 * design. Tabs are in-page state, switched via ``clickBefore`` on the
 * matching tab button.
 *
 * Permission-gated tabs (Budget, Operations Access) are excluded for the
 * same reason as the Cargo tour.
 */

const BUNKERS_ROUTE = '/dashboard/trades/fuels';

const bunkersTour = {
    id: 'bunkers',
    title: 'Trade Ops Bunkers',
    description:
        'Walk through Bunkers: shipment overview, fuel enquiries, recaps and delivery creation. Note: bunkers go enquiry -> recap directly, no bid/offer step.',
    estimatedMinutes: 5,
    startRoute: BUNKERS_ROUTE,
    icon: faTint,
    group: 'module',
    steps: [
        {
            target: '[data-tour="tradeops-page"]',
            title: 'Welcome to Trade Ops Bunkers',
            content:
                'This is your Bunkers operations hub. Fuels trading is leaner than solids: an enquiry becomes a recap, which becomes a delivery. No separate bid/offer step.',
            placement: 'center',
            disableBeacon: true,
        },
        {
            // ``.time-navigation-bar`` is position:absolute top-right. We
            // anchor on the element directly (not a wrapper) so floating-ui
            // gets real coordinates. Placement "left" keeps the tooltip
            // inside the viewport since the bar sits in the top-right
            // corner of the page.
            target: '.time-navigation-bar',
            title: 'Year navigation',
            content:
                'Every table on this page is scoped to the year shown here. Move forward or back to inspect historical enquiries, recaps and deliveries. Refresh re-pulls without leaving the year.',
            placement: 'left',
        },
        {
            target: '[data-tour="tradeops-tabs"]',
            title: 'Module tabs',
            content:
                'Bunkers tabs: Shipment Overview, Bunker Enquiries, Trade Recaps, Deliveries, plus - depending on your access - Budget Calculator and Operations Access. There is no Bid/Offer tab on Bunkers.',
            placement: 'bottom',
        },
        {
            target: '[data-tour="tradeops-fuel-enquiries-section"]',
            title: 'Bunker Enquiries tab',
            content:
                'Every Bunkers deal starts as an enquiry - a customer asks for a bunker stem at a specific port and date, and we quote. The enquiry captures customer, vessel, product, quantity, port, supplier and pricing.',
            placement: 'top',
            clickBefore: '[data-tour="tradeops-tab-fuel-enquiries"]',
        },
        {
            target: '[data-tour="tradeops-new-fuel-enquiry"]',
            title: 'Create a new enquiry',
            content:
                'Click New Enquiry to open the enquiry form. Stepper-driven: IMT entity -> Customer -> Credit -> Product -> Ship -> Supplier -> Tonnage -> Third Party. Save & Workflow advances it through the approval stages.',
            placement: 'bottom',
        },
        {
            target: '[data-tour="tradeops-recaps-section"]',
            title: 'Trade Recaps tab',
            content:
                'Once an enquiry is approved and a stem is confirmed, it matures into a recap - the binding contract. Sales recap = we sell fuel, purchase recap = we buy fuel, freight recap = transport arrangements.',
            placement: 'top',
            clickBefore: '[data-tour="tradeops-tab-recaps"]',
        },
        {
            target: '[data-tour="tradeops-recap-actions"]',
            title: 'Create a recap',
            content:
                'Sales / Purchase / Freight - same three flows as Cargo, but the form is tuned for fuels: bunker spec, financial summary panel, funding panel. The recap drives deliveries and financials downstream.',
            placement: 'bottom',
        },
        {
            target: '[data-tour="tradeops-deliveries-section"]',
            title: 'Deliveries tab',
            content:
                'Deliveries track the physical bunkering: vessel name, port, supplier, BDN quantities and dates. A delivery may fulfil one recap or multiple paired recaps on the same lift.',
            placement: 'top',
            clickBefore: '[data-tour="tradeops-tab-deliveries"]',
        },
        {
            target: '[data-tour="tradeops-new-delivery"]',
            title: 'Create a delivery',
            content:
                'Click Create Delivery to start the delivery form. Link recaps, set vessel, supplier and port, capture BDN figures. Status flows from scheduled to delivered to final.',
            placement: 'bottom',
        },
        {
            target: '[data-tour="tradeops-page"]',
            title: 'You are good to go',
            content:
                'That is the Bunkers tour. Enquiry -> Recap -> Delivery is the whole pipeline. Re-run this tour any time from Hamster Training.',
            placement: 'center',
            disableBeacon: true,
            isLast: true,
        },
    ],
};

export default bunkersTour;
